import Button from "@/Components/Button";
import { toWebp } from "@/utils/common";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

interface GameChangerProps {
  data?: any;
  isMobile?: boolean;
}

const GameChanger: React.FC<GameChangerProps> = ({ data }) => {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;
  const cards = data?.cards || [];

  // ✅ Track expanded index
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <>
      <section className="relative z-[1] overflow-hidden pb-[200px] pt-[56px] lg:pt-[80px] xl:pt-[100px] bg-white before:content-[''] before:absolute before:h-[100%] before:inset-0 before:bg-[url('/images/client-bg.png')] before:bg-no-repeat before:bg-[length:100%_100%] before:bg-center before:bg-[#F0F8FF] before:-z-10">
        <div className="container">
          <div className="heading mb-[40px]">
            <h2 className="lg:text-[48px] xl:text-[65px] xm:text-[32px] text-[26px] text-center xl:leading-[83.3px] lg:leading-[62.4px] xm:leading-[41.6px] leading-[35px] font-[700] text-[#040405]">
              {data?.title}
            </h2>
            <p className="text-subtitle-2 font-regular text-secondary-text mt-3 max-w-[980px] mx-auto text-center">
              {data?.description}
            </p>
          </div>
          {data?.cta?.href && data?.cta?.label && (
            <Link className="mt-4 flex justify-center " href={data?.cta?.href}>
            <div className="flex justify-center">
              <Button
                type="button"
                className="flex items-center justify-center gap-2.5 text-subtitle-2 font-semibold transition-all duration-300 group bg-text-button hover:bg-blue-700 cursor-pointer text-primary-text-white px-5 pt-[11px] pb-[9px]"
                label={data?.cta?.label}
              />
              </div>
            </Link>
          )}
        </div>
      </section>

      <div className="pb-[56px] lg:pb-[80px] xl:pb-[100px] mt-[-160px] relative z-40">
        <div className="container">
          {/* Desktop */}
          <div className="xl:block hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[24px] md:gap-[30px]">
              {cards.map((item: any, index: number) => (
                <div
                  key={index}
                  className="group relative overflow-hidden 
                            before:content-[''] before:absolute before:inset-0 
                            before:bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,#000000_100%)] before:z-[3]
                            h-[312px]
                            "
                >
                  {item?.image?.data?.attributes?.url && (
                  <Image
                    src={toWebp(`${baseUrl}${item?.image?.data?.attributes?.url}`)}
                    alt={item.title}
                    width={668}
                    height={302}
                    quality={100}
                    className="w-full h-full mb-[16px] filter group-hover:blur-sm transition-all duration-500 ease-in-out"
                  />
                  )}
                  <div
                    className="game-content p-[52px_30px] absolute h-[270px] 
                                  bottom-[-160px] w-full overflow-hidden 
                                  group-hover:bottom-[20px] group-hover:h-[100%] 
                                  transition-all duration-500 ease-in-out z-10"
                  >
                    <h5 className="mb-[20px] text-white font-[700] text-[24px] xl:text-[36px] md:text-[28px] whitespace-nowrap truncate">
                      {item.title}
                    </h5>
                    <ul className="space-y-[12px] text-white">
                      {item.keypoints.map((keypoint: any, i: number) => (
                        <li
                          key={i}
                          className="relative pl-6 text-[18px] font-[400] before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-[8px] before:h-[8px] before:bg-[#B0D9F7]"
                        >
                          {keypoint.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Accordion */}
          <div className="w-full  block xl:hidden relative z-[1]">
            {cards.map((item: any, index: number) => {
              const isExpanded = expandedIndex === index;
              return (
                <div
                  key={index}
                  className={`border border-[#B0D9F7] mb-[24px] bg-white ${isExpanded ? " md:bg-[url(/images/noise-container-ipad.png)] bg-[url(/images/noise-container-mobile.png)] bg-no-repeat bg-right-top bg-cover" : ""}`}
                >
                  {/* Header */}
                  <div
                    className={`${isExpanded ? "border-b-0" : ""} border-[#B0D9F7]`}
                  >
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="flex justify-between items-center w-full p-[24px] text-left font-semibold text-gray-900"
                    >
                      <h4 className="text-[#040405] text-[24px] font-[700]">
                        {item.title}
                      </h4>
                      <Image
                        src={
                          isExpanded
                            ? "/images/minus-ts.svg"
                            : "/images/plus-ts.svg"
                        }
                        alt={isExpanded ? "Collapse" : "Expand"}
                        width={24}
                        height={24}
                      />
                    </button>
                  </div>

                  {/* Content */}
                  <div
                    className={`transition-all duration-500 ease-in-out overflow-hidden ${
                      isExpanded
                        ? "max-h-[1000px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="px-4 pb-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-[24px]">
                      <div className="mb-4 ">
                        <Image
                          src={toWebp(`${baseUrl}${item?.image?.data?.attributes?.url}`)}
                          alt={item.title}
                          width={800}
                          height={400}
                          quality={100}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <ul className="space-y-5">
                        {item.keypoints.map((keypoint: any, i: number) => (
                          <li
                            key={i}
                            className="relative pl-6 text-[18px] font-[400] before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-[8px] before:h-[8px] before:bg-[#68A9FF] text-[#32343B]"
                          >
                            {keypoint.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* End Mobile */}
        </div>
      </div>
    </>
  );
};

export default GameChanger;
